import React, { useState, useEffect } from 'react';
import { Avatar, AvatarProps, Skeleton } from '@mui/material';
import { font } from '../../utils/fontHelper';

interface InitialsAvatarProps extends Omit<AvatarProps, 'children'> {
  src?: string;
  fallbackSrc?: string; // Additional fallback photo source (e.g., direct Google Auth photoURL)
  name?: string;
  size?: number;
  fallbackColor?: string;
}

const InitialsAvatar: React.FC<InitialsAvatarProps> = ({
  src,
  fallbackSrc,
  name,
  size = 40,
  fallbackColor = '#6B73FF',
  sx,
  ...props
}) => {
  const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Function to optimize Google photo URLs for better CORS compatibility
  const optimizeGooglePhotoUrl = (url?: string): string | undefined => {
    if (!url) return undefined;

    // Check if it's a Google photo URL
    if (url.includes('googleusercontent.com') || url.includes('lh3.googleusercontent.com')) {
      try {
        // Remove size parameters and add CORS-friendly ones
        let optimizedUrl = url.split('=s')[0]; // Remove size parameter

        // Add size parameter that works better with CORS
        const targetSize = Math.max(size * 2, 96); // Ensure minimum size for quality
        optimizedUrl += `=s${targetSize}-c`;

        return optimizedUrl;
      } catch (error) {
        console.warn('InitialsAvatar: Failed to optimize Google photo URL:', error);
        return url;
      }
    }

    return url;
  };

  // Build array of photo sources to try in order, avoiding duplicates
  const buildPhotoSources = () => {
    const sources: string[] = [];
    const seenUrls = new Set<string>();

    const optimizedSrc = optimizeGooglePhotoUrl(src);
    const optimizedFallback = optimizeGooglePhotoUrl(fallbackSrc);

    if (optimizedSrc && !seenUrls.has(optimizedSrc)) {
      sources.push(optimizedSrc);
      seenUrls.add(optimizedSrc);
    }

    if (optimizedFallback && !seenUrls.has(optimizedFallback)) {
      sources.push(optimizedFallback);
      seenUrls.add(optimizedFallback);
    }

    // Add original URLs only if they're different from optimized versions
    if (src && !seenUrls.has(src)) {
      sources.push(src);
      seenUrls.add(src);
    }

    if (fallbackSrc && !seenUrls.has(fallbackSrc)) {
      sources.push(fallbackSrc);
      seenUrls.add(fallbackSrc);
    }

    return sources;
  };

  const photoSources = buildPhotoSources();

  // Reset when photo sources change
  useEffect(() => {
    setCurrentSrcIndex(0);
    setHasError(false);
    setIsLoading(!!src || !!fallbackSrc); // Only show loading if there are sources to try
  }, [src, fallbackSrc]);

  // Generate initials from name
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    
    const words = name.trim().split(' ').filter(word => word.length > 0);
    
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    
    // For multiple words, take first letter of first and last word
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  };

  // Generate a consistent color based on the name
  const getBackgroundColor = (name?: string): string => {
    if (!name) return fallbackColor;
    
    // Simple hash function to generate consistent colors
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    // Generate a pleasant color palette
    const colors = [
      '#6B73FF', // Default purple
      '#FF6B6B', // Red
      '#4ECDC4', // Teal
      '#45B7D1', // Blue
      '#96CEB4', // Green
      '#FFEAA7', // Yellow
      '#DDA0DD', // Plum
      '#98D8C8', // Mint
      '#F7DC6F', // Light Yellow
      '#BB8FCE', // Light Purple
    ];
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Handle successful image load
  const handleImageLoad = () => {
    setIsLoading(false);
  };

  // Handle image loading errors - try next source or show initials
  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const currentSrc = photoSources[currentSrcIndex];
    const imgElement = event.target as HTMLImageElement;

    // Image failed to load, trying next source

    // For 429 errors or rate limiting, don't retry Google URLs immediately
    if (currentSrc?.includes('googleusercontent.com')) {
      // Google photo failed (likely rate limited), using initials
      setHasError(true);
      setIsLoading(false);
      return;
    }

    // Try next photo source if available
    if (currentSrcIndex < photoSources.length - 1) {
      // Trying next photo source
      setCurrentSrcIndex(prev => prev + 1);
    } else {
      // All sources failed, show initials
      // All photo sources failed, showing initials
      setHasError(true);
      setIsLoading(false);
    }
  };

  // Get current photo source to try
  const currentPhotoSrc = photoSources[currentSrcIndex];

  // Check if we should show the image or fallback to initials
  // Only exclude unsplash placeholder URLs and cases where all sources failed
  const shouldShowImage = currentPhotoSrc &&
    !hasError &&
    !currentPhotoSrc.includes('unsplash.com') &&
    currentPhotoSrc !== '';

  const initials = getInitials(name);
  const backgroundColor = shouldShowImage && !isLoading ? 'transparent' : getBackgroundColor(name);

  // Show loading skeleton if no image URL or still loading
  if (isLoading && !shouldShowImage) {
    return (
      <Skeleton
        variant="circular"
        width={size}
        height={size}
        sx={{
          ...sx
        }}
      />
    );
  }

  return (
    <Avatar
      src={shouldShowImage ? currentPhotoSrc : undefined}
      onError={handleImageError}
      onLoad={handleImageLoad}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.4, // Scale font with size
        fontWeight: 600,
        fontFamily: font(),
        bgcolor: backgroundColor,
        color: '#FFFFFF',
        border: shouldShowImage ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
        ...sx
      }}
      imgProps={{
        crossOrigin: 'anonymous', // Add CORS support to the img element
        referrerPolicy: 'no-referrer' // Help with Google photo loading
      }}
      {...props}
    >
      {!shouldShowImage && initials}
    </Avatar>
  );
};

export default InitialsAvatar; 